import React, { useState, useCallback, useMemo, useEffect } from "react";
import { useSelector } from "react-redux";
import { makeStyles } from "@civicplus/preamble-ui/lib/Utilities/ThemeHelper";
import PrmblSearchInput, {
  SearchInputProps as PrmblSearchInputProps,
} from "@civicplus/preamble-ui/lib/SearchInput";
import { SearchSubFilter } from "../../../types/types.search";
import debounce from "lodash/debounce";
import { RootState } from "../../../app/store";
import { lighten } from "@material-ui/core";

const useStyles = makeStyles(theme => ({
  search: {
    "& fieldset": {
      borderColor: lighten(theme.palette.grey["600"], 0.2),
    },
    "& .prmbl-formControl": {
      [theme.breakpoints.only("xs")]: {
        marginTop: 0,
      },
      [theme.breakpoints.between("sm", "md")]: {
        margin: 0,
      },
    },
  },
}));

// Filter show all from SearchSubFilter
const CONTENT_TYPES = Object.values(SearchSubFilter).filter(
  value => value !== SearchSubFilter.showAll
);
export interface SearchInputProps extends PrmblSearchInputProps {
  onSearch: (search: string, contentTypes?: string[]) => void;
}
const SearchInput: React.FC<SearchInputProps> = props => {
  const { onSearch, ...rest } = props;
  const classes = useStyles();
  const eventFilters = useSelector((state: RootState) => state.eventFilters);
  const searchParam = window.location.search;
  const [search, setSearch] = useState(eventFilters.search || "");
  const [filters, setFilters] = useState<string[]>(
    eventFilters.contentTypes || []
  );
  const firstRender = React.useRef(true);

  const getContentTypesForApi = useCallback((value: string[]) => {
    if (value.length === CONTENT_TYPES.length) {
      return [SearchSubFilter.showAll];
    }
    return value;
  }, []);

  useEffect(() => {
    setFilters(eventFilters.contentTypes || []);
  }, [eventFilters.contentTypes]);

  const getContentTypesForInput = useCallback(() => {
    if (filters.length === 1 && filters[0] === SearchSubFilter.showAll) {
      return CONTENT_TYPES;
    }
    return filters;
  }, [filters]);

  const handleSearch = useCallback(
    (search: string, filters: string[]) => {
      if (search !== "*") {
        setSearch(search);
      }

      setFilters(filters);
      const contentTypesSearch = getContentTypesForApi(filters);
      const debouncedSearch = debounce(onSearch, 500);
      debouncedSearch(search, contentTypesSearch);
      return () => {
        debouncedSearch.cancel();
      };
    },
    [getContentTypesForApi, onSearch]
  );

  useEffect(() => {
    // If the search query is different than the current search, update the search.
    const currentQuery = new URLSearchParams(searchParam)?.get("q");
    const currentFilters = new URLSearchParams(searchParam)?.get("filters");
    if (
      currentQuery &&
      currentQuery !== search &&
      window.location.pathname === "/search" &&
      firstRender.current
    ) {
      let filteredFilters: string[] = [];
      if (currentFilters) {
        const currentFiltersArr = currentFilters.split(",");
        // includes in array only values from enum SearchSubFilter
        filteredFilters = currentFiltersArr.filter(value =>
          Object.values(SearchSubFilter).includes(value as SearchSubFilter)
        );
      }
      handleSearch(currentQuery, filteredFilters);
    }
    firstRender.current = false;
  }, [handleSearch, search, searchParam]);

  const handleFilterChange = useCallback(
    (value: string) => {
      const filtersSelected = getContentTypesForInput();
      const currentIndex = filtersSelected.indexOf(value);
      const newChecked = [...filtersSelected];
      if (currentIndex === -1) {
        newChecked.push(value);
      } else {
        newChecked.splice(currentIndex, 1);
      }

      setFilters(newChecked);
      handleSearch(search, newChecked);
    },
    [getContentTypesForInput, handleSearch, search]
  );

  const handleFilterReset = () => {
    const filtersSelected = getContentTypesForInput();
    if (CONTENT_TYPES.length === filtersSelected.length) {
      if (window.location.pathname !== "/search") {
        setFilters([]);
      } else {
        handleSearch(search, []);
      }
    } else {
      handleSearch(search, CONTENT_TYPES);
    }
  };

  const contentTypesMapped = useMemo(
    () =>
      CONTENT_TYPES.map(value => {
        return {
          id: value,
          checked: getContentTypesForInput().indexOf(value) !== -1,
          onChange: () => handleFilterChange(value),
          label: value.replace(/([a-z])([A-Z])/g, "$1 $2"),
        };
      }),
    [getContentTypesForInput, handleFilterChange]
  );

  return (
    <PrmblSearchInput
      value={search}
      onChange={({ target }: React.ChangeEvent<HTMLInputElement>) => {
        setSearch(target.value);
      }}
      id="search"
      //Allows certain browsers to recommend previously entered search phrases within the portal
      inputProps={{ name: "portalSearch", "aria-labelledby": "search-label" }}
      onSearch={() => handleSearch(search, filters)}
      activeFilters={getContentTypesForInput().length}
      onFilterReset={handleFilterReset}
      filters={contentTypesMapped}
      fullWidth={true}
      classes={{ outerWrapper: classes.search }}
      {...rest}
    />
  );
};

export default SearchInput;
